<template>
  <div class="row wrapper border-bottom white-bg page-heading">
    <div class="col-lg-10">
      <h2>Zamówienia</h2>
      <ol class="breadcrumb">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li class="active">
          <router-link to="/order/list">Zamówienia</router-link>
        </li>
        <li class="active">
          <strong>Szczegóły</strong>
        </li>
      </ol>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight">
    <div class="row">
      <div class="col-lg-12">
        <div v-if="orderData?.data" class="ibox float-e-margins">
          <button
            v-if="orderData.data.can_be_duplicated && canDuplicateOrder"
            :disabled="isDuplicating"
            @click="mDuplicateConfirm = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="copy" class="table-helper-item" />
            <span>{{ isDuplicating ? "Duplikowanie..." : "Duplikuj" }}</span>
          </button>
          <button
            v-if="
              canUpdateOrder &&
              orderData.data.is_external &&
              !orderData.data.duplicated_from
            "
            :disabled="
              isDisableExternalOrderConfirm(orderData.data) ||
              isGeneratingProforma
            "
            @click="externalOrderConfirm"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="file" class="table-helper-item" />
            <span>{{
              isGeneratingProforma ? "Generowanie..." : "Generuj pro-formę"
            }}</span>
          </button>
          <button
            v-if="canAddInvoice && isVisibleIssueVatInvoice(orderData.data)"
            :disabled="isIssuingVatInvoice"
            @click="mIssueVatInvoice = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="file-invoice" class="table-helper-item" />
            <span>{{
              isIssuingVatInvoice ? "Wystawianie..." : "Wystaw fakturę VAT"
            }}</span>
          </button>
          <button
            v-if="!orderData.data.duplicated_from"
            :disabled="isDisablePaid(orderData.data)"
            @click="mPayment = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="dollar-sign" class="table-helper-item" />
            <span>Zapłacono</span>
          </button>
          <button
            :disabled="isDisableCompleted(orderData.data)"
            @click="mCompleted = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="check" class="table-helper-item" />
            <span>Zakończ zamówienie</span>
          </button>
          <button
            :disabled="isDisableCanceled(orderData.data)"
            @click="mCancel = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="times" class="table-helper-item" />
            <span>Anuluj zamówienie</span>
          </button>
          <button
            v-if="!orderData.data.duplicated_from"
            :disabled="isDisableStopDelivery(orderData.data)"
            @click="mSDelivery = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="stop" class="table-helper-item" />
            <span>Zablokuj wysyłkę</span>
          </button>
          <button
            v-if="!orderData.data.duplicated_from"
            :disabled="isDisableUnstopDelivery(orderData.data)"
            @click="mUDelivery = true"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="play" class="table-helper-item" />
            <span>Odblokuj wysyłkę</span>
          </button>
          <button @click="updateAction('/admin_card/')" class="btn btn-primary">
            <font-awesome-icon icon="file" class="table-helper-item" />
            <span>Pobierz kartę zamówienia</span>
          </button>
          <div class="ibox-title" @click="isDetailsOpen = !isDetailsOpen">
            <h5>Szczegóły</h5>
            <span class="ibox-toggle">
              <i :class="chevronDetailsClass"></i>
            </span>
          </div>

          <div class="form-horizontal">
            <div class="ibox-content panel" v-show="isDetailsOpen">
              <div class="panel-body">
                <dl class="dl-horizontal dl-order-detail">
                  <dt>Numer zamówienia:</dt>
                  <dd>{{ orderData.data.pk }}</dd>
                  <template v-if="orderData.data.duplicated_from">
                    <dt>Duplikat zamówienia:</dt>
                    <dd>
                      <router-link
                        :to="{
                          name: 'OrderDetails',
                          params: { pk: orderData.data.duplicated_from.pk },
                        }"
                      >
                        {{ orderData.data.duplicated_from.pk }}
                      </router-link>
                    </dd>
                  </template>
                  <template v-if="orderData.data.allegro_external_id">
                    <dt>Numer zamówienia Allegro:</dt>
                    <dd>{{ orderData.data.allegro_external_id }}</dd>
                  </template>
                  <dt>Status:</dt>
                  <dd>{{ orderData.data.status_display }}</dd>
                  <dt>Termin realizacji:</dt>
                  <dd>{{ orderData.data.realization_date ?? "-" }}</dd>
                  <dt>Data zamówienia / ostatniej modyfikacji:</dt>
                  <dd>
                    {{ orderData.data.date_added ?? "-" }} /
                    {{ orderData.data.date_modified ?? "-" }}
                  </dd>
                  <dt>Sklep / Język / Waluta:</dt>
                  <dd>
                    {{ orderData.data.company_localization_display ?? "-" }} /
                    {{ orderData.data.company_localization_display ?? "-" }} /
                    {{ orderData.data.currency ?? "-" }}
                  </dd>
                  <dt>Waga zamówienia:</dt>
                  <dd>{{ orderData.data.weight }} kg</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="canViewNote" class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Notatki</h5>
            <div class="ibox-tools">
              <button
                v-if="canAddNote"
                @click="openNoteModal()"
                class="btn btn-sm btn-primary"
              >
                <font-awesome-icon
                  icon="note-sticky"
                  class="table-helper-item"
                />
                <span>Dodaj notatkę</span>
              </button>
            </div>
          </div>

          <div class="ibox-content panel">
            <div v-if="loadingNotes" class="text-center">
              <i class="fa fa-spinner fa-spin fa-2x"></i>
            </div>

            <div v-else class="panel-body">
              <p v-if="!orderNotes.length">Brak notatek.</p>

              <ul v-else class="sortable-list agile-list">
                <li
                  v-for="note in orderNotes"
                  :key="note.pk"
                  :class="note.can_edit ? 'success-element' : 'info-element'"
                >
                  <div class="clearfix">
                    <div class="float-end">
                      <button
                        v-if="note.can_edit && canChangeNote"
                        @click="openNoteModal(note)"
                        class="btn btn-xs btn-white"
                      >
                        <font-awesome-icon icon="pencil" />
                        <span> Edytuj</span>
                      </button>
                      <button
                        v-if="note.can_delete && canDeleteNote"
                        @click="mDelNote = note.pk"
                        class="btn btn-xs btn-white"
                      >
                        <font-awesome-icon icon="trash" />
                        <span> Usuń</span>
                      </button>
                    </div>

                    <strong>
                      <font-awesome-icon icon="user" />
                      {{ note.created_by ?? "-" }}
                    </strong>
                  </div>

                  <div v-if="note.tags?.length" class="m-t-xxs">
                    <span
                      v-for="(tag, index) in note.tags"
                      :key="index"
                      class="badge bg-secondary"
                    >
                      {{ tag.name }}
                    </span>
                  </div>

                  <div class="m-t-sm" v-html="note.text"></div>

                  <div class="agile-detail">
                    <font-awesome-icon icon="clock" />
                    Utworzono: {{ note.date_added }}
                    <span
                      v-if="
                        note.date_modified &&
                        note.date_modified !== note.date_added
                      "
                    >
                      | Edytowano: {{ note.date_modified }}
                      <span v-if="note.last_modified_by">
                        <font-awesome-icon icon="user" />
                        {{ note.last_modified_by }}
                      </span>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Dane klienta</h5>
          </div>
          <div class="ibox-content panel">
            <div
              v-for="(client, key) in orderData.data?.client.addresses"
              :key="key"
              class="panel-body"
            >
              <div v-if="client.is_main">
                <span class="dblock">{{ client.company_name }}</span>
                <span class="dblock">{{ client.tax_id }}</span>
                <span class="dblock">
                  {{ client.address }}
                  {{ client.building_number }} /
                  {{ client.suite_number }}
                </span>
                <span class="dblock">
                  {{ client.zip_code }}
                  {{ client.city }}
                </span>
                <span class="dblock">{{ client.country }}</span>
                <br />
                <span class="dblock">
                  {{ orderData.data?.client.user.first_name }}
                  {{ orderData.data?.client.user.last_name }}
                </span>
                <span class="dblock">
                  {{ client.telephone }}
                </span>
                <span class="dblock">
                  {{ orderData.data?.client.user.email }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Faktury</h5>
          </div>
          <div class="ibox-content panel">
            <div v-if="orderData.data" class="panel-body">
              <dl class="dl-horizontal dl-order-detail">
                <dt>Faktura papierowa wysyłana pocztą:</dt>
                <dd>{{ orderData.data?.is_invoice_paper ? "Tak" : "Nie" }}</dd>

                <hr v-if="isAnyInvoice" />
                <dt v-if="isAnyInvoice">Wystawione faktury:</dt>
                <dd>
                  <a
                    v-if="orderData.data.proforma_invoice"
                    @click="getInvoice(orderData.data.pk, '/proforma_invoice/')"
                    class="link-without-href"
                  >
                    Faktura proforma
                  </a>
                  <br />
                  <a
                    v-if="orderData.data.advance_invoice"
                    @click="getInvoice(orderData.data.pk, '/advance_invoice/')"
                    class="link-without-href"
                  >
                    Faktura zaliczkowa
                  </a>
                  <br />
                  <a
                    v-if="orderData.data.final_invoice"
                    @click="getInvoice(orderData.data.pk, '/final_invoice/')"
                    class="link-without-href"
                  >
                    Faktura VAT końcowa
                  </a>
                  <br />
                  <a
                    v-if="orderData.data.vat_invoice"
                    @click="getInvoice(orderData.data.pk, '/vat_invoice/')"
                    class="link-without-href"
                  >
                    Faktura VAT
                  </a>
                </dd>

                <hr />
                <dt>Dane do faktury:</dt>
                <dd>
                  <span>{{ orderData.data?.invoice_address.name }}</span>
                  <br />
                  <span>{{ orderData.data?.invoice_address.nip }}</span>
                  <br />
                  <span>
                    {{ orderData.data?.invoice_address.street }}
                    {{ orderData.data?.invoice_address.building_number }} /
                    {{ orderData.data?.invoice_address.suite_number }}
                  </span>
                  <br />
                  <span>
                    {{ orderData.data?.invoice_address.zip_code }}
                    {{ orderData.data?.invoice_address.city }}
                  </span>
                  <br />
                  <span>{{ orderData.data?.invoice_address.country }}</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Płatność</h5>
          </div>
          <div class="ibox-content panel">
            <div class="panel-body">
              <dl class="dl-horizontal dl-order-detail">
                <dt>Metoda:</dt>
                <dd>{{ orderData.data?.payment_method_display }}</dd>
                <dt>Status:</dt>
                <dd>{{ orderData.data?.payment_status_display }}</dd>
                <dt>Grupa rabatowa:</dt>
                <dd>{{ orderData.data?.discount_group || "—" }}</dd>
                <template
                  v-if="orderData.data && orderData.data.negotiated !== null"
                >
                  <dt>Negocjacje przyjęte:</dt>
                  <dd>{{ orderData.data?.negotiated ? "Tak" : "Nie" }}</dd>
                </template>
                <template v-if="orderData.data?.negotiated">
                  <dt>Wynegocjowany rabat:</dt>
                  <dd>{{ orderData.data?.negotiated_discount }}%</dd>
                </template>
                <hr />
                <dt>Razem netto (dotyczy wartości produktów w koszyku):</dt>
                <dd>{{ orderData.data?.info.net_price }} PLN</dd>
                <dt>Faktura papierowa wysłana pocztą:</dt>
                <dd>{{ orderData.data?.info.paper_invoice_price }} PLN</dd>
                <dt>Koszt wysyłki:</dt>
                <dd>{{ orderData.data?.info.delivery_price }} PLN</dd>
                <dt>Koszt dodatkowych adresów:</dt>
                <dd>
                  {{ orderData.data?.info.additional_addresses_price }} PLN
                </dd>
                <dt>Razem netto:</dt>
                <dd>{{ orderData.data?.info.final_net_price }} PLN</dd>
                <dt>Opłata dystrybucyjna 9%:</dt>
                <dd>{{ orderData.data?.info.distribution_fee_price }} PLN</dd>
                <dt>Podatek VAT 23%:</dt>
                <dd>{{ orderData.data?.info.vat_price }} PLN</dd>
                <dt>Razem</dt>
                <dd>{{ orderData.data?.info.final_gross_price }} PLN</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Wysyłka</h5>
          </div>
          <div class="ibox-content panel">
            <div class="panel-body">
              <dl class="dl-horizontal dl-order-detail">
                <dt>Opcja wysyłki:</dt>
                <dd>
                  {{ orderData.data?.ups_delivery_service_display }}
                  <font-awesome-icon
                    v-if="
                      orderData.data?.is_external &&
                      !orderData.data?.proforma_invoice
                    "
                    icon="pencil"
                    class="inline-edit-icon"
                    title="Edytuj opcję wysyłki"
                    @click="openDeliveryMethodModal"
                  />
                </dd>
                <dt>Status:</dt>
                <dd>{{ orderData.data?.delivery_status_display }}</dd>
                <dt>Powód blokady wysyłki:</dt>
                <dd>{{ orderData.data?.delivery_lock_reason ?? "-" }}</dd>
                <dt>Wysyłka z moim adresem jako nadawcy:</dt>
                <dd>
                  {{ orderData.data?.my_address_as_sender ? "Tak" : "Nie" }}
                </dd>
                <hr />
                <dt>Liczba pozycji zamówienia:</dt>
                <dd>{{ orderData.data?.order_products.length }}</dd>
                <hr />
                <dt>Liczba adresów dostawy:</dt>
                <dd>{{ orderData.data?.delivery_addresses.length }}</dd>
                <dt>Liczba paczek:</dt>
                <dd>{{ orderData.data?.delivery_packages }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Pozycje zamówienia</h5>
          </div>
          <div v-if="items.length > 0" class="ibox-content">
            <EasyDataTable
              v-model:server-options="serverOptions"
              :server-items-length="itemsAll"
              :headers="headers"
              :items="items"
              :clickRowToExpand="false"
              :loading="loading"
              alternating
              multi-sort
              border-cell
              show-index
            >
              <template #item-options="item">
                <span
                  v-for="(option, index) in item.chosen_options"
                  :key="index"
                  class="show m-b-xs dblock"
                >
                  <span class="m-r-xs label label-default">
                    {{ option.chosen_option.group_pk }}
                  </span>
                  <span class="m-r-xs">
                    {{ option.chosen_option.group_name }}
                  </span>
                  <span class="m-r-xs label label-default">
                    {{ option.chosen_option.pk }}
                  </span>

                  <span class="m-r-xs">
                    {{ option.chosen_option.label }}
                  </span>
                </span>
              </template>
              <template #item-actions="item">
                <button
                  v-if="
                    orderData.data?.can_edit_order_products &&
                    canChangeOrderProduct
                  "
                  @click="openEditProductModal(item)"
                  class="btn btn-xs"
                  title="Edytuj pozycję"
                >
                  <i class="fa fa-pencil"></i>
                </button>
                <button
                  v-if="item.is_allegro_source && canAddOrderProductFile"
                  @click="openProductFileModal(item)"
                  class="btn btn-xs"
                  title="Wyślij plik projektu"
                >
                  <i class="fa fa-upload"></i>
                </button>
                <button
                  :disabled="isClientFileNotAvailable(item)"
                  @click="
                    getOrderProductFiles(
                      item?.product_file?.preflight_status?.file_pk,
                      '/get_client_file/'
                    )
                  "
                  class="btn btn-xs"
                  title="Plik Klienta"
                >
                  <i class="fa fa-user"></i>
                </button>
                <button
                  :disabled="isResultFileNotAvailable(item)"
                  @click="
                    getOrderProductFiles(
                      item?.product_file?.preflight_status?.file_pk,
                      '/get_result_file/'
                    )
                  "
                  class="btn btn-xs"
                  title="Plik wyjściowy"
                >
                  <i class="fa fa-search"></i>
                </button>
                <button
                  v-if="!isClientCoverFileNotAvailable(item)"
                  @click="
                    getOrderProductFiles(
                      item?.product_file?.preflight_status?.file_pk,
                      '/get_client_cover_file/'
                    )
                  "
                  class="btn btn-xs"
                  title="Plik okładki klienta"
                >
                  <i class="fa fa-user"></i>
                </button>
                <button
                  v-if="!isCoverResultFileNotAvailable(item)"
                  @click="
                    getOrderProductFiles(
                      item?.product_file?.preflight_status?.file_pk,
                      '/get_cover_result_file/'
                    )
                  "
                  class="btn btn-xs"
                  title="Plik okładki wyjściowy"
                >
                  <i class="fa fa-search"></i>
                </button>
              </template>
              <template #item-preview="item">
                <div class="order-product-preview">
                  <img
                    v-if="getClientThumbnail(item)"
                    :src="getClientThumbnail(item)"
                    @click="preview(item)"
                    width="40px"
                  />
                  <img
                    v-if="getClientThumbnailSecondPage(item)"
                    :src="getClientThumbnailSecondPage(item)"
                    @click="preview(item)"
                    width="40px"
                  />
                  <!-- Helper icons of the preview column, as in the old admin
                       panel: the preflight status and the accepted preview -->
                  <span class="preview-helpers">
                    <span
                      class="preflight-status-icon"
                      :class="item.product_file?.preflight_status?.class"
                      :title="item.product_file?.preflight_status?.message"
                    ></span>
                    <span
                      v-if="item.product_file?.preview_accepted"
                      class="preflight-accept-icon fa fa-check-circle text-navy"
                      title="Podgląd zaakceptowany"
                    ></span>
                  </span>
                </div>
              </template>
              <template #item-montage_pk="item">
                <span
                  v-if="item.montage_pk"
                  class="chosen-pk"
                  @mouseenter="showMontageLogs($event, item.montage_pk)"
                  @mouseleave="hideMontageLogs"
                >
                  <router-link
                    :to="{
                      name: 'MontagesEdit',
                      params: { pk: item.montage_pk },
                    }"
                  >
                    {{ item.montage_pk }}
                  </router-link>
                  <Teleport to="body">
                    <div
                      v-if="activeMontagePk === item.montage_pk"
                      class="montage-logs-popover"
                      :style="montageLogsPopoverStyle"
                    >
                      <div class="row">
                        <div class="col-sm-12">
                          <h4>Logi produkcyjne</h4>
                        </div>
                      </div>
                      <div
                        v-if="isMontageLogsLoading(item.montage_pk)"
                        class="row"
                      >
                        <div class="col-sm-12">Ładowanie...</div>
                      </div>
                      <div v-else class="row">
                        <div class="col-sm-12">
                          <table class="table table-condensed table-hover">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Akcja</th>
                                <th>Zmiana statusu</th>
                                <th>Użytkownik</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="log in getMontageLogsForPk(
                                  item.montage_pk
                                )"
                                :key="log.pk"
                              >
                                <td class="text-muted">{{ log.created }}</td>
                                <td>{{ log.action_name }}</td>
                                <td
                                  v-if="
                                    log.status_values?.old &&
                                    log.status_values?.new
                                  "
                                >
                                  {{ log.status_values.old }}
                                  &rarr;
                                  {{ log.status_values.new }}
                                </td>
                                <td v-else>—</td>
                                <td>{{ log.user?.username }}</td>
                              </tr>
                              <tr
                                v-if="
                                  !getMontageLogsForPk(item.montage_pk)?.length
                                "
                              >
                                <td colspan="4" class="text-center">
                                  Brak logów
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </Teleport>
                </span>
              </template>
            </EasyDataTable>
          </div>
        </div>
      </div>
      <div
        v-if="orderData.data?.allegro_external_id && canViewSourceFile"
        class="col-lg-12"
      >
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Pliki źródłowe</h5>
          </div>
          <div class="ibox-content panel">
            <div
              v-for="orderProduct in orderData.data.order_products"
              :key="orderProduct.pk"
              class="source-files-item"
            >
              <h4>
                ID zlecenia: {{ orderProduct.pk }} |
                {{ orderProduct.product_name_no_tags }}
                <button
                  v-if="canAddSourceFile"
                  @click="openSourceFileModal(orderProduct)"
                  class="btn btn-link btn-sm"
                >
                  <span class="fa fa-upload"></span>
                  <span> Dodaj plik</span>
                </button>
              </h4>

              <table
                v-if="orderProduct.source_files?.length"
                class="table table-condensed"
              >
                <thead>
                  <tr>
                    <th>Plik</th>
                    <th style="width: 200px">Dodany przez</th>
                    <th style="width: 200px">Data dodania</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="sourceFile in orderProduct.source_files"
                    :key="sourceFile.uuid"
                  >
                    <td>
                      <span v-if="sourceFile.is_removed" class="text-muted">
                        <font-awesome-icon icon="ban" />
                        {{ sourceFile.file_name }}
                        <small>(plik usunięty)</small>
                      </span>
                      <a
                        v-else
                        :href="sourceFile.file"
                        target="_blank"
                        download=""
                      >
                        <font-awesome-icon icon="download" />
                        {{ sourceFile.file_name }}
                      </a>
                    </td>
                    <td>
                      {{ sourceFile.created_by_role_display || "Nieznany" }}
                    </td>
                    <td>{{ sourceFile.date_added }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!orderProduct.source_files?.length">
                Brak przesłanych plików
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="orderData.data?.allegro_external_id" class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Wiadomości Allegro</h5>
          </div>
          <div class="ibox-content panel">
            <table
              v-if="orderAllegroMessages.length"
              class="table table-condensed"
            >
              <thead>
                <tr>
                  <th style="width: 250px">Nazwa konta</th>
                  <th>ID wątku</th>
                  <th style="width: 250px">Data ostatniej widomości</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="message in orderAllegroMessages" :key="message.pk">
                  <td>
                    <router-link
                      :to="{
                        name: 'AllegroAccountThreads',
                        params: { pk: message.account.pk },
                      }"
                    >
                      {{ message.account.name }}
                    </router-link>
                  </td>
                  <td>
                    <router-link
                      :to="{
                        name: 'AllegroThreadDetails',
                        params: { pk: message.pk },
                      }"
                    >
                      {{ message.external_id }}
                    </router-link>
                  </td>
                  <td>{{ message.last_message_date_time }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else>Brak wątków.</div>
          </div>
        </div>
      </div>
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Adresy dostawy</h5>
          </div>
          <div class="ibox-content panel">
            <div v-if="orderData.data" class="panel-body">
              <div
                v-for="(order, index) in orderData.data.delivery_addresses"
                :key="index"
                class="row"
              >
                <div class="col-md-4">
                  <div class="form-inline">
                    <div class="form-group">
                      <div class="none">
                        <input
                          v-model="order.ups_tracking_number"
                          :modelValue="order.ups_tracking_number"
                          type="text"
                          class="form-control"
                          placeholder="Numer trackingowy"
                        />
                      </div>
                    </div>
                    <button
                      @click="
                        updateTrackingModal(order.pk, order.ups_tracking_number)
                      "
                      class="btn btn-primary"
                    >
                      Zapisz
                    </button>
                  </div>
                  <hr class="hr-line-dashed" />
                  <div class="row">
                    <div
                      v-for="(pack, index1) in order.delivery_packages"
                      :key="index1"
                      class="col-md-12"
                    >
                      <h3>
                        <span>{{ pack.package.name }}</span>
                      </h3>
                      <p class="small">
                        <i class="fa fa-truck"></i>
                        <span>{{ pack.package.height }}</span>
                        <i class="fa fa-times"></i>
                        <span>{{ pack.package.width }}</span>
                        <i class="fa fa-times"></i>
                        <span>{{ pack.package.length }}</span>
                        <span> [cm]</span>
                      </p>
                    </div>
                  </div>

                  <div v-if="order.target_point" class="m-b-sm">
                    <hr class="hr-line-dashed" />
                    <h4>Wybrany paczkomat</h4>
                    <dl>
                      <dt>Nazwa:</dt>
                      <dd>{{ order.target_point.name }}</dd>
                      <dt>Adres:</dt>
                      <dd>{{ order.target_point.full_address }}</dd>
                    </dl>
                  </div>

                  <hr class="hr-line-dashed" />
                  <div>
                    <span>{{ order.name }}</span>
                    <br />
                    <span>{{ order.street }}</span>
                    <span>{{ order.building_number }}</span>
                    <br />
                    <span>{{ order.zip_code }}</span>
                    <span>{{ order.city }}</span>
                    <br />
                    <span>{{ order.country }}</span>
                    <br />
                    <span>{{ order.telephone }}</span>
                    <br />
                    <button @click="changeAddress()" class="btn btn-warning">
                      Zmień adres
                    </button>
                  </div>
                </div>
                <div class="col-md-8">
                  <EasyDataTable
                    v-model:server-options="serverOptions"
                    :server-items-length="order.products.length"
                    :headers="addressHeaders"
                    :items="order.products"
                    :clickRowToExpand="false"
                    :loading="loading"
                    alternating
                    multi-sort
                    show-index
                    border-cell
                  >
                  </EasyDataTable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Historia zmian</h5>
          </div>
          <div v-if="orderLogsData?.data?.results?.length" class="ibox-content">
            <EasyDataTable
              v-model:server-options="serverLogsOptions"
              :server-items-length="orderLogsData.data.results.length"
              :headers="logsHeaders"
              :items="orderLogsData.data.results"
              :clickRowToExpand="false"
              :loading="loading"
              alternating
              show-index
              multi-sort
              border-cell
            >
            </EasyDataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mPreview" class="modal-dialog preflight">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Podgląd pliku</h4>
        </div>
        <div v-if="previewData.preview.product_file" class="modal-body">
          <pdfViewer
            :read-only="!previewData.is_allegro_source"
            :order-product-file="previewData.preview.product_file"
            :gross-size="previewData.preview.product_gross_size"
            :bleed="previewData.product_bleed"
            :text-margin="previewData.product_text_margin"
            :product-kind="previewData.product_kind"
            :tech-lines="previewData.preview.tech_lines"
            :booklet-uv="previewData.preview.is_booklet_uv"
            :pages-loaded="0"
            :loading="true"
            loading-text="Ładowanie stron"
            @file-page-change="onPreviewAcceptedChange"
          />
        </div>
        <div class="modal-footer">
          <button @click="mPreview = false" class="btn btn-white">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mPreview" @click="mPreview = false" class="modal-backdrop"></div>

  <div v-if="mTracking" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz zapisać numer trackingowy?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mTracking = false" class="btn btn-white">Nie</button>
          <button @click="updateTracking" class="btn btn-primary">Tak</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mTracking" @click="mTracking = false" class="modal-backdrop"></div>

  <div v-if="mAddress" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Edycja adresu dostawy</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="form-group">
              <label class="control-label col-sm-2">Nazwa</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.name"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Nazwa"
                  required="required"
                />
                <div v-if="!addressObj.name" class="has-error">
                  <span class="help-block">To pole nie może być puste.</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Numer telefonu</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.telephone"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Numer telefonu"
                  required="required"
                />
                <div v-if="!addressObj.telephone" class="has-error">
                  <span class="help-block">To pole nie może być puste.</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Ulica</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.street"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Ulica"
                  required="required"
                />
                <div v-if="!addressObj.street" class="has-error">
                  <span class="help-block">To pole nie może być puste.</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Numer budynku</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.building_number"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Numer budynku"
                  required="required"
                />
                <div v-if="!addressObj.building_number" class="has-error">
                  <span class="help-block">To pole nie może być puste.</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Numer mieszkania</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.suite_number"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Numer mieszkania"
                />
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Miasto</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.city"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Miasto"
                  required="required"
                />
                <div v-if="!addressObj.city" class="has-error">
                  <span class="help-block">To pole nie może być puste.</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Kod pocztowy</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.zip_code"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Kod pocztowy"
                  required="required"
                />
                <div v-if="!addressObj.zip_code" class="has-error">
                  <span class="help-block">To pole nie może być puste.</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label col-sm-2">Kraj</label>
              <div class="col-sm-10">
                <input
                  v-model="addressObj.country"
                  type="text"
                  class="form-control inputClass"
                  placeholder="Kraj"
                  required="required"
                  :disabled="true"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mAddress = false" class="btn btn-white">
            Anuluj
          </button>
          <button @click="saveAddress" class="btn btn-primary">Zapisz</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mAddress" @click="mAddress = false" class="modal-backdrop"></div>

  <div v-if="mPayment" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz zmienić status zamówieńia na «Opłacone»?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mPayment = false" class="btn btn-white">Nie</button>
          <button
            @click="updateAction('/admin_pay_an_order/')"
            class="btn btn-primary"
          >
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mPayment" @click="mPayment = false" class="modal-backdrop"></div>

  <div v-if="mDuplicateConfirm" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz zduplikować zamówienie o nr:
                <strong>{{ orderData.data.pk }}</strong
                >?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mDuplicateConfirm = false" class="btn btn-white">
            Nie
          </button>
          <button
            @click="duplicateOrder"
            :disabled="isDuplicating"
            class="btn btn-primary"
          >
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mDuplicateConfirm"
    @click="mDuplicateConfirm = false"
    class="modal-backdrop"
  ></div>

  <div v-if="mCompleted" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz zmienić status zamówienia na «Zrealizowane»?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mCompleted = false" class="btn btn-white">Nie</button>
          <button
            @click="updateAction('/admin_complete_an_order/')"
            class="btn btn-primary"
          >
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mCompleted"
    @click="mCompleted = false"
    class="modal-backdrop"
  ></div>

  <div v-if="mIssueVatInvoice" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz wystawić fakturę VAT dla tego zamówienia?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mIssueVatInvoice = false" class="btn btn-white">
            Nie
          </button>
          <button @click="confirmIssueVatInvoice" class="btn btn-primary">
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mIssueVatInvoice"
    @click="mIssueVatInvoice = false"
    class="modal-backdrop"
  ></div>

  <div v-if="mCancel" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz zmienić status zamówienia na «Anulowane»?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mCancel = false" class="btn btn-white">Nie</button>
          <button
            @click="updateAction('/admin_cancel_an_order/')"
            class="btn btn-primary"
          >
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mCancel" @click="mCancel = false" class="modal-backdrop"></div>

  <div v-if="mUDelivery" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie działania</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                Czy na pewno chcesz odblokować wysyłke zamówienia?
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mUDelivery = false" class="btn btn-white">Nie</button>
          <button
            @click="updateAction('/admin_unstop_delivery/')"
            class="btn btn-primary"
          >
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mUDelivery"
    @click="mUDelivery = false"
    class="modal-backdrop"
  ></div>

  <div v-if="mDelNote" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie usunięcia</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">Czy na pewno chcesz usunąć notatkę?</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mDelNote = false" class="btn btn-white">Nie</button>
          <button @click="deleteNote" class="btn btn-danger">Tak</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mDelNote" @click="mDelNote = false" class="modal-backdrop"></div>

  <div v-if="mSDelivery" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Zablokowanie wysyłki</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                <div class="form-group">
                  <label class="control-label col-sm-2">
                    Powód blokady wysyłki
                  </label>
                  <div class="col-sm-10 QuillEditor-box">
                    <Editor
                      api-key="9csaeb3mljywong9mh5nhj1vdunzpft9enx56len33n2ly2f"
                      v-model="lockReason"
                      :init="{
                        toolbar_mode: 'sliding',
                        plugins:
                          'anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks code wordcount',
                        toolbar:
                          'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
                      }"
                    />
                    <div
                      v-if="errorsAdminData?.data?.lockReason"
                      class="has-error"
                    >
                      <span class="help-block">
                        {{ errorsAdminData.data.lockReason[0] }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mSDelivery = false" class="btn btn-white">
            Zamknij
          </button>
          <button
            @click="updateAction('/admin_stop_delivery/')"
            class="btn btn-danger"
          >
            Zablokuj
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mSDelivery"
    @click="mSDelivery = false"
    class="modal-backdrop"
  ></div>

  <div v-if="mNote" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">
            {{
              mNote === true
                ? "Dodanie notatki zamówienia"
                : "Edycja notatki zamówienia"
            }}
          </h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                <div class="form-group">
                  <label class="control-label col-sm-2">Treść</label>
                  <div class="col-sm-10 QuillEditor-box">
                    <Editor
                      api-key="9csaeb3mljywong9mh5nhj1vdunzpft9enx56len33n2ly2f"
                      v-model="noteValue"
                      :init="{
                        toolbar_mode: 'sliding',
                        plugins:
                          'anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks code wordcount',
                        toolbar:
                          'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
                      }"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <label class="control-label col-sm-2">Tagi</label>
                  <div class="col-sm-10">
                    <Multiselect
                      v-model="noteTags"
                      mode="tags"
                      placeholder="Tagi"
                      track-by="name"
                      label="name"
                      :close-on-select="false"
                      :searchable="true"
                      :options="tags"
                    >
                    </Multiselect>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mNote = false" class="btn btn-white">Anuluj</button>
          <button
            v-if="mNote === true"
            @click="addNote"
            class="btn btn-primary"
          >
            Zapisz
          </button>
          <button v-else @click="updateNote" class="btn btn-primary">
            Zapisz
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="mNote" @click="mNote = false" class="modal-backdrop"></div>

  <OrderProductFileModal
    v-if="fileOrderProduct && mProductFile"
    :order-product="fileOrderProduct"
    @close="closeProductFileModal"
    @uploaded="onProductFileUploaded"
  />

  <OrderProductSourceFileModal
    v-if="fileOrderProduct && mSourceFile"
    :order-product="fileOrderProduct"
    @close="closeSourceFileModal"
    @uploaded="onSourceFileUploaded"
  />

  <div v-if="mDeliveryMethod" class="modal-dialog preview-modal">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Edycja opcji wysyłki</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="row">
              <div class="col-lg-12">
                <div v-if="deliveryErrors?.nonFieldErrors" class="has-error">
                  <span
                    v-for="(error, index) in deliveryErrors.nonFieldErrors"
                    :key="index"
                    class="help-block"
                  >
                    {{ error }}
                  </span>
                </div>

                <div class="form-group">
                  <label class="control-label col-sm-3">Metoda dostawy</label>
                  <div class="col-sm-9">
                    <select
                      v-model="deliveryMethodValue"
                      @change="deliveryChanged"
                      class="form-control"
                    >
                      <option
                        v-for="option in deliveryOptions"
                        :key="option[0]"
                        :value="option[0]"
                      >
                        {{ option[1] }}
                      </option>
                    </select>
                    <div
                      v-if="deliveryErrors?.ups_delivery_service"
                      class="has-error"
                    >
                      <span
                        v-for="(
                          error, index
                        ) in deliveryErrors.ups_delivery_service"
                        :key="index"
                        class="help-block"
                      >
                        {{ error }}
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="isLockerService" class="form-group">
                  <label class="control-label col-sm-3">
                    Nazwa paczkomatu
                  </label>
                  <div class="col-sm-9">
                    <input
                      v-model="targetPointName"
                      type="text"
                      class="form-control"
                      required="required"
                    />
                    <div
                      v-if="deliveryErrors?.target_point_name"
                      class="has-error"
                    >
                      <span
                        v-for="(
                          error, index
                        ) in deliveryErrors.target_point_name"
                        :key="index"
                        class="help-block"
                      >
                        {{ error }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mDeliveryMethod = false" class="btn btn-default">
            Zamknij
          </button>
          <button
            @click="saveDeliveryMethod"
            :disabled="isSubmittingDelivery"
            class="btn btn-primary"
          >
            {{ isSubmittingDelivery ? "Zapisywanie..." : "Zapisz" }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mDeliveryMethod"
    @click="mDeliveryMethod = false"
    class="modal-backdrop"
  ></div>

  <div
    v-if="mEditProduct"
    class="modal-dialog preview-modal edit-product-modal"
  >
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">
            Edycja pozycji zamówienia {{ editingProduct?.pk }}
          </h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="row">
              <div class="col-lg-12">
                <div class="form-group">
                  <label class="control-label col-sm-3">Nakład</label>
                  <div class="col-sm-9">
                    <label class="radio-inline">
                      <input
                        type="radio"
                        v-model="useCustomQuantity"
                        :value="false"
                      />
                      Standardowy
                    </label>
                    <label class="radio-inline">
                      <input
                        type="radio"
                        v-model="useCustomQuantity"
                        :value="true"
                      />
                      Własny
                    </label>
                  </div>
                </div>

                <div v-if="!useCustomQuantity" class="form-group">
                  <label class="control-label col-sm-3">Wybierz nakład</label>
                  <div class="col-sm-9">
                    <Multiselect
                      v-model="selectedQuantityPk"
                      mode="single"
                      value-prop="pk"
                      track-by="display_quantity"
                      label="display_quantity"
                      :close-on-select="true"
                      :searchable="true"
                      :options="editingProduct?.product_quantities || []"
                    />
                  </div>
                </div>
                <div v-else class="form-group">
                  <label class="control-label col-sm-3">Własny nakład</label>
                  <div class="col-sm-9">
                    <input
                      v-model.number="userQuantity"
                      type="number"
                      min="0"
                      class="form-control"
                    />
                  </div>
                </div>

                <div v-if="loadingGroupOptions" class="text-center">
                  <i class="fa fa-spinner fa-spin fa-2x"></i>
                </div>

                <div
                  v-for="group in groupOptions"
                  :key="group.group_option.pk"
                  class="form-group"
                >
                  <label class="control-label col-sm-3">
                    {{ group.group_option.name }}
                  </label>
                  <div class="col-sm-9">
                    <Multiselect
                      v-model="selectedOptions[group.group_option.pk]"
                      :mode="group.group_option.is_multiple ? 'tags' : 'single'"
                      value-prop="pk"
                      track-by="label"
                      label="label"
                      :close-on-select="!group.group_option.is_multiple"
                      :searchable="true"
                      :options="group.options"
                    />
                    <div
                      v-for="option in getSelectedOptionsWithInput(group)"
                      :key="option.pk"
                      class="m-t-xs"
                    >
                      <label>
                        Wartość dla: {{ option.label }} (zakres
                        {{ option.min_value }}-{{ option.max_value }})
                      </label>
                      <input
                        v-model.number="optionUserValues[option.pk]"
                        type="number"
                        class="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="mEditProduct = false" class="btn btn-default">
            Zamknij
          </button>
          <button
            @click="saveEditProduct"
            :disabled="isSubmittingProduct"
            class="btn btn-primary"
          >
            {{ isSubmittingProduct ? "Zapisywanie..." : "Zapisz" }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="mEditProduct"
    @click="mEditProduct = false"
    class="modal-backdrop"
  ></div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import pdfViewer from "../pdfViewer/pdfViewer.vue";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import Editor from "@tinymce/tinymce-vue";
import OrderProductFileModal from "./OrderProductFileModal.vue";
import OrderProductSourceFileModal from "./OrderProductSourceFileModal.vue";
import { hasPermission } from "../../permissions";
import {
  orderPermissionsConst,
  orderproductPermissionsConst,
  orderProductFilePermissionsConst,
  sourceFilePermissionsConst,
  invoicePermissionsConst,
} from "../../constants/orderConstants";
import { notesPermissionsConst } from "../../constants/noteConstants";

export default {
  name: "OrderDetails",
  mixins: [],
  components: {
    pdfViewer,
    Multiselect,
    Editor,
    OrderProductFileModal,
    OrderProductSourceFileModal,
  },
  props: {},

  setup() {
    const errorsAdminData = ref();
    const showErrors = ref(false);
    const store = useStore();
    const orderData = computed(() => store.getters.getOrderData);
    const orderLogsData = computed(() => store.getters.getOrderLogsData);
    const updateTrackingData = computed(
      () => store.getters.getUpdateTrackingData
    );
    const orderProductsFileData = computed(
      () => store.getters.getOrderProductsFileData
    );
    const montageProductionLogsData = computed(
      () => store.getters.getMontageProductionLogsData
    );
    const clientAllegroThreadsData = computed(
      () => store.getters.getClientAllegroThreadsData
    );
    const actionsOrderData = computed(() => store.getters.getActionsOrderData);
    const tagsData = computed(() => store.getters.getTagsData);
    const notesData = computed(() => store.getters.getNotesData);
    const orderNotesData = computed(() => store.getters.getOrderNotesData);
    const orderNotes = ref([]);
    const loadingNotes = ref(true);
    const chevronDetailsClass = computed(() =>
      isDetailsOpen.value ? "fa fa-chevron-up" : "fa fa-chevron-down"
    );
    const clientsOptions = ref([]);
    const adminsComplaint = ref([]);
    const countriesOptions = ref([]);
    let items = ref([]);
    const loading = ref(true);
    const mPreview = ref(false);
    const mTracking = ref(false);
    const mPayment = ref(false);
    const mCancel = ref(false);
    const mCompleted = ref(false);
    const mIssueVatInvoice = ref(false);
    const isIssuingVatInvoice = ref(false);
    const isGeneratingProforma = ref(false);
    const mSDelivery = ref(false);
    const mUDelivery = ref(false);
    const mAddress = ref(false);
    const mNote = ref(false);
    const mDelNote = ref(false);
    const previewData = ref();
    const trackingPk = ref();
    const lockReason = ref();
    const noteValue = ref();
    const noteTags = ref([]);
    const isDetailsOpen = ref(true);
    const trackingNumber = ref();
    const tags = ref([]);
    const orderAllegroMessages = ref([]);
    const addressObj = ref({});
    let itemsAll = ref(0);
    const route = useRoute();
    const router = useRouter();
    const serverOptions = ref({
      page: 1,
      rowsPerPage: 25,
    });
    const serverLogsOptions = ref({
      page: 1,
      rowsPerPage: 25,
    });
    let headers = [
      { text: "Podgląd", value: "preview", sortable: true, search: true },
      { text: "ID Zlecenia", value: "pk", sortable: true, search: true },
      { text: "Nr montażu", value: "montage_pk", sortable: true, search: true },
      {
        text: "ID produktu",
        value: "product_pk",
        sortable: true,
        search: true,
      },
      { text: "Produkt", value: "product_name", sortable: true, search: true },
      { text: "Nakład", value: "selected_quantity", sortable: true },
      { text: "Format", value: "format", sortable: true, search: true },
      { text: "Netto", value: "net_price", sortable: true, search: true },
      { text: "Waluta", value: "currency", sortable: true, search: true },
      { text: "Waga", value: "weight", sortable: true, search: true },
      { text: "Opcje", value: "options", sortable: true, search: true },
      { text: "Akcje", value: "actions" },
    ];
    let addressHeaders = [
      { text: "ID Zlecenia", value: "pk" },
      { text: "Nr montażu", value: "montage_pk" },
      { text: "ID produktu", value: "order_product_pk" },
      { text: "Produkt", value: "product_name" },
      { text: "Nakład", value: "amount" },
      { text: "Waga", value: "weight" },
      { text: "Waga wysłana", value: "real_weight" },
    ];
    let logsHeaders = [
      { text: "Autor", value: "author_display" },
      { text: "Status", value: "status_display" },
      { text: "Status płatności", value: "payment_status_display" },
      { text: "Status dostawy", value: "delivery_status_display" },
      { text: "Data", value: "created" },
    ];

    // --- Edycja opcji wysyłki (external orders) ---
    const mDeliveryMethod = ref(false);
    const deliveryOptions = ref([]);
    const deliveryMethodValue = ref(null);
    const targetPointName = ref(null);
    const isLockerService = ref(false);
    const isSubmittingDelivery = ref(false);
    const deliveryErrors = ref({});

    const deliveryOptionsData = computed(
      () => store.getters.getDeliveryOptionsData
    );
    const updateExternalData = computed(
      () => store.getters.getUpdateExternalData
    );

    const deliveryChanged = () => {
      isLockerService.value = !!(
        deliveryMethodValue.value &&
        deliveryMethodValue.value.indexOf("inpost_locker_") === 0
      );

      if (!isLockerService.value) {
        targetPointName.value = null;
        delete deliveryErrors.value.target_point_name;
      }
    };

    const openDeliveryMethodModal = () => {
      deliveryErrors.value = {};
      deliveryMethodValue.value = orderData.value.data.ups_delivery_service;

      const firstAddress = orderData.value.data.delivery_addresses?.[0];
      targetPointName.value = firstAddress?.target_point?.name ?? null;

      store.dispatch("getOrderDeliveryOptions", route.params?.pk);
      mDeliveryMethod.value = true;
    };

    const saveDeliveryMethod = () => {
      isSubmittingDelivery.value = true;
      store.dispatch("updateOrderDeliveryMethod", {
        pk: route.params?.pk,
        ups_delivery_service: deliveryMethodValue.value,
        target_point_name: targetPointName.value,
      });
    };
    // --- Koniec: edycja opcji wysyłki ---

    // --- Current admin permissions ---
    const canDuplicateOrder = computed(() =>
      hasPermission(orderPermissionsConst.duplicate)
    );
    const canUpdateOrder = computed(() =>
      hasPermission(orderPermissionsConst.update)
    );
    const canAddInvoice = computed(() =>
      hasPermission(invoicePermissionsConst.add)
    );
    const canChangeOrderProduct = computed(() =>
      hasPermission(orderproductPermissionsConst.update)
    );
    const canAddOrderProductFile = computed(() =>
      hasPermission(orderProductFilePermissionsConst.add)
    );
    const canViewSourceFile = computed(() =>
      hasPermission(sourceFilePermissionsConst.view)
    );
    const canAddSourceFile = computed(() =>
      hasPermission(sourceFilePermissionsConst.add)
    );
    const canViewNote = computed(() =>
      hasPermission(notesPermissionsConst.view)
    );
    const canAddNote = computed(() => hasPermission(notesPermissionsConst.add));
    const canChangeNote = computed(() =>
      hasPermission(notesPermissionsConst.update)
    );
    const canDeleteNote = computed(() =>
      hasPermission(notesPermissionsConst.delete)
    );
    // --- End: current admin permissions ---

    // --- Duplikacja zamówienia ---
    const mDuplicateConfirm = ref(false);
    const isDuplicating = ref(false);
    const duplicateOrderData = computed(
      () => store.getters.getDuplicateOrderData
    );
    const externalOrderConfirmData = computed(
      () => store.getters.getExternalOrderConfirmData
    );

    const duplicateOrder = () => {
      isDuplicating.value = true;
      store.dispatch("duplicateOrder", route.params?.pk);
    };
    // --- Koniec: duplikacja zamówienia ---

    // --- Edycja pozycji zamówienia ---
    const mEditProduct = ref(false);
    const editingProduct = ref(null);
    const useCustomQuantity = ref(false);
    const selectedQuantityPk = ref(null);
    const userQuantity = ref(null);
    const selectedOptions = ref({});
    const optionUserValues = ref({});
    const groupOptions = ref([]);
    const loadingGroupOptions = ref(false);
    const isSubmittingProduct = ref(false);

    const adminGPData = computed(() => store.getters.getAdminGPData);
    const updateOrderProductData = computed(
      () => store.getters.getUpdateOrderProductData
    );

    const groupHasOptionWithInput = (group) =>
      (group.options || []).some(
        (option) => option.enter_values_possible === true
      );

    const getSelectedOptionsWithInput = (group) => {
      const selected = selectedOptions.value[group.group_option.pk];
      if (!selected) return [];
      const pks = Array.isArray(selected) ? selected : [selected];
      return group.options.filter(
        (option) => pks.includes(option.pk) && option.enter_values_possible
      );
    };

    const initEditProductSelectedOptions = () => {
      selectedOptions.value = {};
      optionUserValues.value = {};

      groupOptions.value.forEach((group) => {
        const groupPk = group.group_option.pk;
        const isMultiple = group.group_option.is_multiple;
        const chosenForGroup = (
          editingProduct.value?.chosen_options || []
        ).filter((chosen) => chosen.chosen_option.group_pk === groupPk);

        chosenForGroup.forEach((chosen) => {
          const option = group.options.find(
            (opt) => opt.pk === chosen.chosen_option.pk
          );
          if (option?.enter_values_possible) {
            optionUserValues.value[option.pk] = chosen.user_value;
          }
        });

        if (chosenForGroup.length === 0) {
          if (isMultiple) {
            selectedOptions.value[groupPk] = [];
          }
          return;
        }

        const pks = chosenForGroup.map((chosen) => chosen.chosen_option.pk);
        selectedOptions.value[groupPk] = isMultiple ? pks : pks[0];
      });
    };

    const openEditProductModal = (item) => {
      editingProduct.value = item;
      useCustomQuantity.value = !item.selected_quantity_pk;
      selectedQuantityPk.value = item.selected_quantity_pk || null;
      userQuantity.value = item.user_quantity ?? null;
      selectedOptions.value = {};
      optionUserValues.value = {};
      groupOptions.value = [];
      loadingGroupOptions.value = true;

      store.dispatch("getAdminGP", item.product_pk);
      mEditProduct.value = true;
    };

    const getEditProductValidationError = () => {
      if (!useCustomQuantity.value && !selectedQuantityPk.value) {
        return "Wybierz nakład.";
      }

      if (
        useCustomQuantity.value &&
        (userQuantity.value === null ||
          userQuantity.value === undefined ||
          userQuantity.value < 0)
      ) {
        return "Podaj poprawny własny nakład.";
      }

      let missingGroupName = null;
      groupOptions.value.forEach((group) => {
        if (missingGroupName || groupHasOptionWithInput(group)) return;

        const selected = selectedOptions.value[group.group_option.pk];
        if (!selected || (Array.isArray(selected) && selected.length === 0)) {
          missingGroupName = group.group_option.name;
        }
      });
      if (missingGroupName) {
        return `Wybierz opcję dla: ${missingGroupName}.`;
      }

      let optionValueError = null;
      groupOptions.value.forEach((group) => {
        if (optionValueError) return;

        const selected = selectedOptions.value[group.group_option.pk];
        const pks = Array.isArray(selected)
          ? selected
          : selected
          ? [selected]
          : [];

        pks.forEach((pk) => {
          if (optionValueError) return;

          const option = group.options.find((opt) => opt.pk === pk);
          if (!option || !option.enter_values_possible) return;

          const userValue = optionUserValues.value[option.pk];
          if (userValue === null || userValue === undefined) {
            optionValueError = `Podaj wartość dla opcji "${option.label}".`;
            return;
          }

          if (
            (option.min_value !== null &&
              option.min_value !== undefined &&
              userValue < option.min_value) ||
            (option.max_value !== null &&
              option.max_value !== undefined &&
              userValue > option.max_value)
          ) {
            optionValueError = `Wartość opcji "${option.label}" musi być w zakresie ${option.min_value}-${option.max_value}.`;
          }
        });
      });

      return optionValueError;
    };

    const getChosenOptionsPayload = () => {
      const allOptions = groupOptions.value.flatMap((group) => group.options);
      const payload = [];

      Object.values(selectedOptions.value).forEach((selected) => {
        if (!selected) return;
        const pks = Array.isArray(selected) ? selected : [selected];

        pks.forEach((pk) => {
          const option = allOptions.find((opt) => opt.pk === pk);
          payload.push({
            chosen_option: pk,
            user_value: option?.enter_values_possible
              ? optionUserValues.value[pk]
              : null,
          });
        });
      });

      return payload;
    };

    const saveEditProduct = () => {
      const validationError = getEditProductValidationError();
      if (validationError) {
        store.dispatch("showToaster", {
          type: "error",
          content: validationError,
        });
        return;
      }

      isSubmittingProduct.value = true;
      store.dispatch("updateOrderProduct", {
        pk: editingProduct.value.pk,
        data: {
          selected_quantity: useCustomQuantity.value
            ? null
            : selectedQuantityPk.value,
          user_quantity: useCustomQuantity.value ? userQuantity.value : null,
          chosen_options: getChosenOptionsPayload(),
        },
      });
    };
    // --- Koniec: edycja pozycji zamówienia ---

    // --- Order product files (project and source) ---
    // The modals themselves live in their own components, so their internal
    // state - notably the preflight polling - never re-renders this view.
    const mProductFile = ref(false);
    const mSourceFile = ref(false);
    const fileOrderProduct = ref(null);

    const isClientFileNotAvailable = (item) =>
      !item?.product_file?.client_file_url;
    const isResultFileNotAvailable = (item) =>
      !item?.product_file?.preflight_result;
    const isClientCoverFileNotAvailable = (item) =>
      !item?.product_file?.client_cover_file_url;
    const isCoverResultFileNotAvailable = (item) =>
      !item?.product_file?.cover_preflight_result;

    const openProductFileModal = (orderProduct) => {
      fileOrderProduct.value = orderProduct;
      mProductFile.value = true;
    };

    const closeProductFileModal = () => {
      mProductFile.value = false;
      fileOrderProduct.value = null;
    };

    // Only the touched row is refreshed - reloading the whole order would
    // rebuild the products table just to show one new thumbnail.
    const onProductFileUploaded = ({ orderProductPk, productFile }) => {
      const row = items.value.find((item) => item.pk === orderProductPk);
      if (row) {
        row.product_file = productFile;
      }
    };

    const openSourceFileModal = (orderProduct) => {
      fileOrderProduct.value = orderProduct;
      mSourceFile.value = true;
    };

    const closeSourceFileModal = () => {
      mSourceFile.value = false;
      fileOrderProduct.value = null;
    };

    const onSourceFileUploaded = () => {
      store.dispatch("getOrder", route.params?.pk);
    };
    // --- End: order product files ---

    const getOrderData = () => {
      loading.value = true;
      store.dispatch("getOrderLogs", {
        page: serverLogsOptions.value.page,
        itemsPerPage: serverLogsOptions.value.rowsPerPage,
        order_pk: route.params?.pk,
      });
    };

    const getOrderProductFiles = (pk, type) => {
      if (pk) {
        store.dispatch("getOrderProductFiles", {
          pk: pk,
          type: type,
        });
      }
    };

    const preview = (item) => {
      previewData.value = item;
      store.dispatch("getOrderProductsFile", { pk: item.pk });
    };

    // The preview modal works on a separately fetched file, so the acceptance
    // flag has to be copied back onto the row to refresh its column icon.
    const onPreviewAcceptedChange = (previewAccepted) => {
      if (previewData.value?.product_file) {
        previewData.value.product_file.preview_accepted = previewAccepted;
      }
    };

    const getClientThumbnail = (item) =>
      item?.product_file?.client_file_thumbnail ||
      item?.product_file?.thumbnail ||
      item?.product_file?.preflight_status?.client_file_thumbnail ||
      null;

    const getClientThumbnailSecondPage = (item) =>
      item?.product_file?.client_file_thumbnail_2 ||
      item?.product_file?.client_file_thumbnail_page_2 ||
      item?.product_file?.thumbnail_2 ||
      item?.product_file?.preflight_status?.client_file_thumbnail_2 ||
      item?.product_file?.preflight_status?.client_file_thumbnail_page_2 ||
      null;

    // --- Popover logów produkcyjnych montażu (Nr montażu w tabeli pozycji) ---
    // Renderowany przez <Teleport to="body">, bo tabela (EasyDataTable) ma
    // overflow: auto i przycinałaby popover, gdyby został w jej wnętrzu.
    const activeMontagePk = ref(null);
    const montageLogsLoadingPk = ref(null);
    const montageLogsCache = ref({});
    const montageLogsPopoverStyle = ref({});

    const showMontageLogs = (event, pk) => {
      if (!pk) {
        return;
      }
      activeMontagePk.value = pk;
      const rect = event.currentTarget.getBoundingClientRect();
      montageLogsPopoverStyle.value = {
        top: `${rect.top + rect.height / 2}px`,
        left: `${rect.right + 8}px`,
        transform: "translateY(-50%)",
      };
      if (montageLogsCache.value[pk] || montageLogsLoadingPk.value === pk) {
        return;
      }
      montageLogsLoadingPk.value = pk;
      store.dispatch("getMontageProductionLogs", { pk });
    };

    const hideMontageLogs = () => {
      activeMontagePk.value = null;
    };

    const getMontageLogsForPk = (pk) => montageLogsCache.value[pk];

    const isMontageLogsLoading = (pk) =>
      activeMontagePk.value === pk &&
      montageLogsLoadingPk.value === pk &&
      !montageLogsCache.value[pk];

    watch(
      () => montageProductionLogsData.value,
      (newValue) => {
        const pk = newValue?.montage_pk;
        if (!pk) {
          return;
        }
        const data = newValue.data;
        montageLogsCache.value[pk] = Array.isArray(data)
          ? data
          : data?.results || [];
        if (montageLogsLoadingPk.value === pk) {
          montageLogsLoadingPk.value = null;
        }
      }
    );

    const updateTracking = () => {
      store.dispatch("updateTracking", {
        pk: trackingPk.value,
        ups_tracking_number: trackingNumber.value,
      });
    };

    const saveAddress = () => {
      store.dispatch("updateTracking", addressObj.value);
    };

    const updateTrackingModal = (pk, tracking) => {
      trackingPk.value = pk;
      mTracking.value = true;
      trackingNumber.value = tracking;
    };

    const changeAddress = () => {
      mAddress.value = true;
    };

    const updateAction = (typeV) => {
      let actionObj = {};
      if (lockReason.value) {
        actionObj = {
          pk: route.params?.pk,
          type: typeV,
          delivery_lock_reason: lockReason.value,
        };
      } else {
        actionObj = {
          pk: route.params?.pk,
          type: typeV,
        };
      }
      store.dispatch("orderActions", actionObj);
      lockReason.value = "";
    };

    const isDisablePaid = (order) => {
      if (order.payment_method === "deadline") {
        return order.payment_status === "paid" || order.status !== "done";
      }

      if (order.is_external) {
        return (
          (order.status !== "waiting_for_payment" &&
            order.status !== "in_realization" &&
            order.status !== "in_production") ||
          order.payment_method === "after_receiving" ||
          order.payment_status === "paid"
        );
      } else {
        return (
          order.status !== "waiting_for_payment" ||
          order.payment_method === "after_receiving"
        );
      }
    };

    const isDisableCompleted = (order) => {
      return (
        order.status !== "in_realization" && order.status !== "in_production"
      );
    };

    const isDisableCanceled = (order) => {
      return (
        order.status === "canceled" ||
        order.status === "in_production" ||
        order.status === "done" ||
        order.status === "deleted"
      );
    };

    const isDisableStopDelivery = (order) => {
      return order.delivery_status !== "not_send";
    };

    const isDisableUnstopDelivery = (order) => {
      return order.delivery_status !== "stopped";
    };

    const isDisableExternalOrderConfirm = (order) => {
      return !!order.proforma_invoice;
    };

    const externalOrderConfirm = () => {
      if (!canUpdateOrder.value) {
        return;
      }
      isGeneratingProforma.value = true;
      store.dispatch("externalOrderConfirm", route.params?.pk);
    };

    const isVisibleIssueVatInvoice = (order) => {
      return (
        !order.vat_invoice &&
        order.payment_method === "after_receiving" &&
        order.status === "done"
      );
    };

    const confirmIssueVatInvoice = () => {
      if (!canAddInvoice.value) {
        return;
      }
      isIssuingVatInvoice.value = true;
      updateAction("/admin_issue_vat_invoice/");
    };

    const isAnyInvoice = computed(() => {
      const order = orderData.value?.data;
      return !!(
        order?.proforma_invoice ||
        order?.advance_invoice ||
        order?.final_invoice ||
        order?.vat_invoice
      );
    });

    const fetchOrderNotes = () => {
      if (!canViewNote.value || !orderData.value?.data?.content_type_pk) {
        loadingNotes.value = false;
        return;
      }

      loadingNotes.value = true;
      store.dispatch("getOrderNotes", {
        content_type: orderData.value.data.content_type_pk,
        object_id: orderData.value.data.pk,
      });
    };

    const addNote = () => {
      let noteObj = {
        content_type: orderData.value?.data?.content_type_pk,
        object_id: route.params?.pk,
        tags: noteTags.value,
        text: noteValue.value,
      };
      store.dispatch("addOrderNote", noteObj);
    };

    const deleteNote = () => {
      store.dispatch("delOrderNote", mDelNote.value);
    };

    const openNoteModal = (note) => {
      noteTags.value = [];
      noteValue.value = "";

      if (!note) {
        mNote.value = true;
        return;
      }

      note.tags?.forEach((element) => {
        noteTags.value.push(element.pk);
      });
      noteValue.value = note.text;
      mNote.value = note.pk;
    };

    const updateNote = () => {
      let noteObj = {
        content_type: orderData.value?.data?.content_type_pk,
        object_id: route.params?.pk,
        tags: noteTags.value,
        text: noteValue.value,
        pk: mNote.value,
      };
      store.dispatch("updateOrderNote", noteObj);
    };

    const getInvoice = (pk, type) => {
      store.dispatch("orderActions", {
        type: type,
        pk: pk,
      });
    };

    store.dispatch("getOrder", route.params?.pk);
    store.dispatch("getTags");
    getOrderData();

    watch(
      () => tagsData.value,
      function (newValue) {
        if (newValue.data && newValue.data.length > 0) {
          newValue.data?.forEach((element) => {
            tags.value.push({
              name: element.name,
              value: element.pk,
            });
          });
        }
      }
    );

    watch(
      () => orderData.value,
      function (newValue) {
        if (!newValue?.data) {
          loading.value = false;
          return;
        }

        itemsAll.value = newValue.data.count;
        addressObj.value = newValue.data.delivery_addresses?.[0];
        // Assigned in one go instead of emptying and refilling, which would
        // unmount every row only to mount it again.
        items.value = newValue.data.order_products || [];
        loading.value = false;

        if (newValue.data.client?.pk) {
          store.dispatch("getClientAllegroThreads", {
            client_pk: newValue.data.client.pk,
          });
        }

        fetchOrderNotes();
      }
    );

    watch(
      () => orderNotesData.value,
      function (newValue) {
        orderNotes.value = newValue?.data?.results || [];
        loadingNotes.value = false;
      }
    );

    watch(
      () => clientAllegroThreadsData.value,
      function (newValue) {
        orderAllegroMessages.value = newValue?.data?.results || [];
      }
    );

    watch(
      () => serverLogsOptions.value,
      function () {
        getOrderData();
      }
    );

    watch(
      () => orderProductsFileData.value,
      function (newValue) {
        // An error response carries no data - reading it here used to throw
        // inside the watcher and abort the render flush half way through.
        if (newValue?.data?.product_file) {
          previewData.value = previewData.value || {};
          previewData.value.preview = newValue.data;
          mPreview.value = true;
        }
      }
    );

    watch(
      () => updateTrackingData.value,
      function (newValue) {
        if (newValue.status === 200) {
          if (newValue.type === "address") {
            mAddress.value = false;
            store.dispatch("showToaster", {
              type: "success",
              content: "Adres został zaktualizowany!",
            });
          } else {
            mTracking.value = false;
            store.dispatch("showToaster", {
              type: "success",
              content: "Numer trackingowy został zaktualizowany!",
            });
          }
        }
      }
    );

    watch(
      () => actionsOrderData.value,
      function (newValue) {
        if (newValue.status === 200) {
          store.dispatch("getOrder", route.params?.pk);
          store.dispatch("showToaster", {
            type: "success",
            content:
              newValue.type === "/admin_issue_vat_invoice/"
                ? "Faktura VAT została wystawiona."
                : "Zamówienie zostało zaktualizowane!",
          });
          mTracking.value = false;
          mPayment.value = false;
          mCompleted.value = false;
          mCancel.value = false;
          mSDelivery.value = false;
          mUDelivery.value = false;
          mAddress.value = false;
          mNote.value = false;
          mIssueVatInvoice.value = false;
          isIssuingVatInvoice.value = false;
        } else {
          var msg = [];
          for (const [key, value] of Object.entries(newValue.data)) {
            msg.push(key + ": " + value);
          }

          store.dispatch("showToaster", {
            type: "error",
            content: msg[0],
          });
          errorsAdminData.value = newValue;
          isIssuingVatInvoice.value = false;
        }
      }
    );

    watch(
      () => notesData.value,
      function (newValue) {
        if (newValue.status === 201) {
          fetchOrderNotes();
          store.dispatch("showToaster", {
            type: "success",
            content: "Notatka została Dodana!",
          });
          mNote.value = false;
        } else if (newValue.status === 200) {
          fetchOrderNotes();
          store.dispatch("showToaster", {
            type: "success",
            content: "Notatka została zaktualizowana!",
          });
          mNote.value = false;
        } else if (newValue.status === 204) {
          fetchOrderNotes();
          store.dispatch("showToaster", {
            type: "success",
            content: "Notatka została usunięta!",
          });
          mDelNote.value = false;
        } else {
          var msg = [];
          for (const [key, value] of Object.entries(newValue.data)) {
            msg.push(key + ": " + value);
          }

          store.dispatch("showToaster", {
            type: "error",
            content: msg[0],
          });
          errorsAdminData.value = newValue;
        }
      }
    );

    watch(
      () => deliveryOptionsData.value,
      function (newValue) {
        if (newValue?.status === 200 && newValue?.data?.options) {
          deliveryOptions.value = newValue.data.options;
          deliveryChanged();
        }
      }
    );

    watch(
      () => updateExternalData.value,
      function (newValue) {
        if (!newValue) return;

        if (newValue.status === 200) {
          mDeliveryMethod.value = false;
          isSubmittingDelivery.value = false;
          store.dispatch("getOrder", route.params?.pk);
          store.dispatch("showToaster", {
            type: "success",
            content: "Opcja wysyłki została zaktualizowana!",
          });
        } else {
          isSubmittingDelivery.value = false;
          deliveryErrors.value = newValue.data;

          var msg = [];
          for (const [key, value] of Object.entries(newValue.data)) {
            msg.push(key + ": " + value);
          }
          store.dispatch("showToaster", {
            type: "error",
            content: msg[0],
          });
        }
      }
    );

    watch(
      () => externalOrderConfirmData.value,
      function (newValue) {
        if (!newValue) return;

        isGeneratingProforma.value = false;

        if (newValue.status === 200) {
          store.dispatch("getOrder", route.params?.pk);
          store.dispatch("showToaster", {
            type: "success",
            content: "Pomyślnie stworzono fakturę proforma.",
          });
        } else {
          store.dispatch("showToaster", {
            type: "error",
            content: "Nie udało się stworzyć faktury proforma.",
          });
        }
      }
    );

    watch(
      () => duplicateOrderData.value,
      function (newValue) {
        if (!newValue) return;

        if (newValue.status === 201) {
          mDuplicateConfirm.value = false;
          store.dispatch("showToaster", {
            type: "success",
            content: "Zamówienie zostało zduplikowane!",
          });
          router.push({
            name: "OrderDetails",
            params: { pk: newValue.data.pk },
          });
        } else {
          isDuplicating.value = false;
          store.dispatch("showToaster", {
            type: "error",
            content:
              newValue.data?.non_field_errors?.[0] ??
              "Nie udało się zduplikować zamówienia.",
          });
        }
      }
    );

    watch(
      () => adminGPData.value,
      function (newValue) {
        if (!newValue) return;

        if (newValue.status === 200) {
          groupOptions.value = newValue.data;
          initEditProductSelectedOptions();
        }
        loadingGroupOptions.value = false;
      }
    );

    watch(
      () => updateOrderProductData.value,
      function (newValue) {
        if (!newValue) return;

        if (newValue.status === 200) {
          mEditProduct.value = false;
          isSubmittingProduct.value = false;
          store.dispatch("getOrder", route.params?.pk);
          store.dispatch("showToaster", {
            type: "success",
            content: "Pozycja zamówienia została zaktualizowana!",
          });
        } else {
          isSubmittingProduct.value = false;
          store.dispatch("showToaster", {
            type: "error",
            content:
              newValue.data?.chosen_options?.[0] ??
              newValue.data?.non_field_errors?.[0] ??
              newValue.data?.selected_quantity?.[0] ??
              newValue.data?.user_quantity?.[0] ??
              "Nie udało się zaktualizować pozycji zamówienia.",
          });
        }
      }
    );

    return {
      errorsAdminData,
      showErrors,
      clientsOptions,
      adminsComplaint,
      countriesOptions,
      headers,
      addressHeaders,
      orderData,
      itemsAll,
      items,
      loading,
      serverOptions,
      serverLogsOptions,
      orderLogsData,
      logsHeaders,
      getOrderProductFiles,
      mPreview,
      previewData,
      preview,
      onPreviewAcceptedChange,
      getClientThumbnail,
      getClientThumbnailSecondPage,
      showMontageLogs,
      hideMontageLogs,
      getMontageLogsForPk,
      isMontageLogsLoading,
      activeMontagePk,
      montageLogsPopoverStyle,
      trackingNumber,
      updateTracking,
      mTracking,
      mPayment,
      mCompleted,
      mCancel,
      mSDelivery,
      mUDelivery,
      mAddress,
      mNote,
      mIssueVatInvoice,
      isIssuingVatInvoice,
      confirmIssueVatInvoice,
      updateTrackingModal,
      changeAddress,
      addressObj,
      updateAction,
      isDisablePaid,
      isDisableCompleted,
      isDisableCanceled,
      isDisableStopDelivery,
      isDisableUnstopDelivery,
      isVisibleIssueVatInvoice,
      isAnyInvoice,
      isDisableExternalOrderConfirm,
      externalOrderConfirm,
      isGeneratingProforma,
      lockReason,
      noteValue,
      noteTags,
      addNote,
      tags,
      orderAllegroMessages,
      openNoteModal,
      orderNotes,
      loadingNotes,
      mDelNote,
      deleteNote,
      updateNote,
      getInvoice,
      saveAddress,
      isDetailsOpen,
      chevronDetailsClass,
      // Edycja opcji wysyłki
      mDeliveryMethod,
      deliveryOptions,
      deliveryMethodValue,
      targetPointName,
      isLockerService,
      isSubmittingDelivery,
      deliveryErrors,
      openDeliveryMethodModal,
      saveDeliveryMethod,
      deliveryChanged,
      // Current admin permissions
      canDuplicateOrder,
      canUpdateOrder,
      canAddInvoice,
      canChangeOrderProduct,
      canAddOrderProductFile,
      canViewSourceFile,
      canAddSourceFile,
      canViewNote,
      canAddNote,
      canChangeNote,
      canDeleteNote,
      // Duplikacja zamówienia
      mDuplicateConfirm,
      isDuplicating,
      duplicateOrder,
      // Edycja pozycji zamówienia
      mEditProduct,
      editingProduct,
      useCustomQuantity,
      selectedQuantityPk,
      userQuantity,
      selectedOptions,
      optionUserValues,
      groupOptions,
      loadingGroupOptions,
      isSubmittingProduct,
      openEditProductModal,
      saveEditProduct,
      getSelectedOptionsWithInput,
      // Order product files (project and source)
      mProductFile,
      mSourceFile,
      fileOrderProduct,
      isClientFileNotAvailable,
      isResultFileNotAvailable,
      isClientCoverFileNotAvailable,
      isCoverResultFileNotAvailable,
      openProductFileModal,
      closeProductFileModal,
      onProductFileUploaded,
      openSourceFileModal,
      closeSourceFileModal,
      onSourceFileUploaded,
    };
  },
  computed: {},
};
</script>

<style scoped>
/* Preview cell: thumbnails first, helper icons in their own column. The
   global preflight styles place those icons absolutely over the thumbnail,
   which is undone here so nothing overlaps. */
.order-product-preview {
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: center;
}

.order-product-preview img {
  cursor: pointer;
}

.order-product-preview .preview-helpers {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-product-preview .preflight-status-icon,
.order-product-preview .preflight-accept-icon {
  position: static;
  right: auto;
  top: auto;
}

/* Explicit box instead of auto - with auto the glyph keeps the size from the
   global rule and font-size has no visible effect */
.order-product-preview .preflight-accept-icon {
  font-size: 14px;
  height: 14px;
  line-height: 1;
  width: 14px;
}

/* Separator between source file groups - a border instead of a v-if on an
   <hr> inside the v-for, which would add comment placeholders the renderer
   has to patch on every order reload */
.source-files-item + .source-files-item {
  border-top: 1px solid #e7eaec;
  margin-top: 15px;
  padding-top: 15px;
}

/* Bootstrap 5 nie ma już .btn-xs - odtworzone, żeby przyciski notatek
   miały ten sam rozmiar co w panelu AngularJS (Bootstrap 3) */
.btn-xs {
  padding: 1px 5px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 3px;
}

.ibox-title {
  cursor: pointer;
  position: relative;
}

.ibox-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #676a6c;
}

.ibox-toggle:hover {
  color: #333;
}

.inline-edit-icon {
  cursor: pointer;
  margin-left: 8px;
  color: #676a6c;
}

.inline-edit-icon:hover {
  color: #1ab394;
}

.edit-product-modal {
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.montage-logs-popover {
  position: fixed;
  width: 470px;
  background: #ffffff;
  padding: 10px;
  z-index: 1100;
  border: 2px solid #1ab394;
  border-radius: 6px;
}
</style>
